import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { isNumber, mapKeys, set } from "lodash";
import { MailTemplateState } from "../objects/mailTemplateState";
import { MailTemplateSliceState } from "../objects/mailTemplateSliceState";
import {
    ADD_VISUAL_EDITOR_BLOCK,
    ADD_VISUAL_EDITOR_BLOCKS_WITHOUT_ORDER,
    CREATE_HTML_CODE_EDITOR_LOCALE_INITIAL_CONTENT,
    CREATE_VISUAL_EDITOR_LOCALE_INITIAL_CONTENT,
    DELETE_VISUAL_EDITOR_BLOCK,
    DELETE_VISUAL_EDITOR_BLOCK_FROM_COLUMNS_BLOCK,
    DUPLICATE_VISUAL_EDITOR_BLOCK,
    MailTemplateActionTypes,
    REDO_VISUAL_EDITOR,
    REORDER_VISUAL_EDITOR_BLOCKS,
    RESET_CREATE_FORM,
    RESET_EDITORS,
    RESTORE_VISUAL_EDITOR_BLOCKS_FROM_HTML,
    SELECT_VISUAL_EDITOR_BLOCK,
    SET_AUTOCOMPLETE,
    SET_CREATE_ACTION,
    SET_CREATE_ACTION_CATEGORY,
    SET_CREATE_EXTRA_PARAMS,
    SET_CREATE_FROM_MAIL,
    SET_CREATE_FROM_MAIL_ERROR,
    SET_CREATE_FROM_NAME,
    SET_CREATE_NAME,
    SET_CREATE_CMS,
    SET_CREATE_SUBJECT,
    SET_EDITOR_CHOICE,
    SET_GOOGLE_FONTS,
    SET_HTML_CODE_EDITOR_CONTENT,
    SET_MAIL_TEMPLATES,
    SET_QUERY,
    SET_RECIPIENTS,
    SET_VISUAL_EDITOR_BLOCK_OPTIONS,
    SET_VISUAL_EDITOR_BODY_STYLES,
    SET_VISUAL_EDITOR_DRAGGED_BLOCK_ID,
    SET_VISUAL_EDITOR_MODE,
    TOGGLE_CANCEL_MODAL,
    TOGGLE_CREATE,
    UNDO_VISUAL_EDITOR,
    UNSELECT_VISUAL_EDITOR_BLOCK,
    SET_CREATE_CLIENT_BRANCH,
    SET_COMPANY
} from "./actionTypes";
import { RegisteredBlocks } from "../utils/registered-blocks";
import { rgbToHex } from "../utils/rgbToHex";
import { htmlWalker, MailTemplateBodyHtmlWalkingStrategy } from "../utils/html-walker";
import { createDomObjectsFromHtml } from "../utils/dom";
import { Block } from "../objects/block";
import { GoogleFont } from "../objects/googleFont";
import DefaultHtml from "../assets/mail-template-default.html";
import undoable, { includeAction } from "redux-undo";
import { combineReducers } from "redux";
import { MailTemplateVisualEditorState } from "../objects/mailTemplateVisualEditorState";
import { ListNetworkData } from "../../utils/list/objects/listNetworkData";
import { ListPeriodFilterShortcut } from "../../utils/list/objects/listPeriodFilterShortcut";
import { MailTemplate } from "../objects/mailTemplate";
import { Mode } from "../objects/mode";
import { restorer } from "../blocks/restorer";
import { extractVersionFromHtml } from "../utils/extractVersionFromHtml";
import { ColumnsBlock } from "../blocks/mailTemplateVisualEditorColumnsBlock";

const defaultFont: GoogleFont = {
    name: 'Roboto',
    url: 'https://fonts.googleapis.com/css2?family=Roboto&display=swap&global=true'
};

const initialState: MailTemplateState = {
    cancel_modal: false,
    create: false,
    create_action: null,
    create_action_category: null,
    create_extra_params: {
        activated: true,
        replyable: true
    },
    create_name: [],
    create_cms: [],
    create_client_branches: [],
    create_subject: [],
    create_from_name: null,
    create_from_mail: null,
    create_from_mail_error: '',
    editorChoice: null,
    googleFonts: [],
    queries: [],
    htmlCodeEditorContents: [],
    visualEditorDraggedBlockId: null,
    visualEditorSelectedBlockId: null,
    mailTemplates: null,
    autocomplete: { state: 'loading' },
    recipients: { state: 'loading' },
    company: null
};

const initialMailTemplateSliceState: MailTemplateSliceState = {
    autocomplete: { state: 'loading' },
    createForm: {
        mainRecipient: null,
        ccRecipients: [],
        bccRecipients: [],
        nbDaysList: null
    },
    listData: { state: 'loading' },
    listOrder: {
        by: null,
        direction: 'asc'
    },
    listFilters: {
        period: {
            type: 'CREATION',
            shortcut: ListPeriodFilterShortcut.NONE,
            values: {
                CREATION: {
                    LESS_THAN: new Date().toISOString(),
                    GREATER_THAN: new Date().toISOString(),
                },
                MODIFICATION: {
                    LESS_THAN: new Date().toISOString(),
                    GREATER_THAN: new Date().toISOString(),
                },
            }
        },
        pagination: {
            limit: 50,
            page: 0
        },
        action: null,
        name: null,
        creator: null,
        lastAuthor: null,
        activated: null,
        nbDays: null,
        deliveryTypes: []
    },
};

const initialVisualEditorState: MailTemplateVisualEditorState = {
    visualEditorMode: Mode.DESKTOP,
    visualEditorBlocks: [],
    visualEditorBodyStyles: {
        fontFamily: defaultFont,
        backgroundColor: "#fff",
        width: 600,
        padding: {
            top: 0,
            bottom: 0,
            left: 0,
            right: 0
        },
        innerBorder: {
            color: '#000',
            width: 0
        },
        innerBackgroundColor: '#fff',
        innerPadding: {
            top: 0,
            bottom: 0,
            left: 0,
            right: 0
        }
    },
};

const MailTemplateReducer = (state: MailTemplateState = initialState, action: MailTemplateActionTypes): MailTemplateState => {
    switch (action.type) {
        case CREATE_HTML_CODE_EDITOR_LOCALE_INITIAL_CONTENT: {
            const firstContent = state.htmlCodeEditorContents[0];
            const contents = state.htmlCodeEditorContents.find(({ locale }) => locale === action.payload.locale);

            if (!contents && firstContent) {
                return {
                    ...state,
                    htmlCodeEditorContents: state.htmlCodeEditorContents.concat([
                        {
                            locale: action.payload.locale,
                            content: firstContent.content
                        }
                    ])
                };
            } else if (!contents && !firstContent) {
                return {
                    ...state,
                    htmlCodeEditorContents: state.htmlCodeEditorContents.concat([
                        { locale: action.payload.locale, content: DefaultHtml }
                    ])
                };
            }

            return state;
        }
        case RESET_CREATE_FORM: {
            return {
                ...state,
                create_action: 'USER_VERIFICATION_EMAIL',
                create_action_category: null,
                create_name: [],
                create_cms: [],
                create_client_branches: [],
                create_subject: [],
                create_extra_params: {
                    activated: true,
                    replyable: true
                },
                create_from_mail: null,
                create_from_mail_error: '',
                create_from_name: null
            };
        }
        case SET_MAIL_TEMPLATES: {
            return {
                ...state,
                mailTemplates: action.payload
            };
        }
        case RESET_EDITORS: {
            return {
                ...state,
                editorChoice: null,
                htmlCodeEditorContents: [],
            };
        }
        case SET_AUTOCOMPLETE: {
            return {
                ...state,
                autocomplete: action.payload
            };
        }
        case SET_CREATE_ACTION: {
            return { ...state, create_action: action.payload };
        }
        case SET_CREATE_ACTION_CATEGORY: {
            return { ...state, create_action_category: action.payload };
        }
        case SET_CREATE_EXTRA_PARAMS: {
            return { ...state, create_extra_params: action.payload };
        }
        case SET_CREATE_FROM_MAIL: {
            return { ...state, create_from_mail: action.payload, create_from_mail_error: '' };
        }
        case SET_CREATE_FROM_MAIL_ERROR: {
            return { ...state, create_from_mail_error: action.payload };
        }
        case SET_CREATE_FROM_NAME: {
            return { ...state, create_from_name: action.payload };
        }
        case SET_CREATE_NAME: {
            const current = state.create_name.find((item) => item.locale === action.payload.locale);

            if (!current) {
                return {
                    ...state,
                    create_name: state.create_name.concat([action.payload])
                };
            }

            return {
                ...state,
                create_name: state.create_name.map((item) => {
                    if (item.locale === action.payload.locale) {
                        return {
                            ...item,
                            value: action.payload.value
                        };
                    }
                    return item;
                })
            };
        }
        case SET_CREATE_CMS: {
            return { ...state, create_cms: action.payload };
        }
        case SET_CREATE_SUBJECT: {
            const current = state.create_subject.find((item) => item.locale === action.payload.locale);

            if (!current) {
                return {
                    ...state,
                    create_subject: state.create_subject.concat([action.payload])
                };
            }

            return {
                ...state,
                create_subject: state.create_subject.map((item) => {
                    if (item.locale === action.payload.locale) {
                        return {
                            ...item,
                            value: action.payload.value
                        };
                    }
                    return item;
                })
            };
        }
        case SET_EDITOR_CHOICE: {
            return { ...state, editorChoice: action.payload };
        }
        case SET_GOOGLE_FONTS: {
            return { ...state, googleFonts: action.payload };
        }
        case SET_HTML_CODE_EDITOR_CONTENT: {
            const index = state.htmlCodeEditorContents.findIndex((item) => {
                return item.locale === action.payload.locale;
            });

            if (index < 0) {
                return {
                    ...state,
                    htmlCodeEditorContents: state.htmlCodeEditorContents.concat([action.payload])
                };
            }

            return {
                ...state,
                htmlCodeEditorContents: [
                    ...state.htmlCodeEditorContents.slice(0, index),
                    action.payload,
                    ...state.htmlCodeEditorContents.slice(index + 1)
                ]
            };
        }
        case SET_QUERY: {
            const currentQuery = state.queries.find((query) => query.key === action.payload.key);
            if (currentQuery) {
                return {
                    ...state,
                    queries: state.queries.map((query) => query.key === action.payload.key ? action.payload : query)
                };
            }
            return {
                ...state,
                queries: [...state.queries, action.payload]
            };
        }
        case SET_RECIPIENTS: {
            return {
                ...state,
                recipients: action.payload
            };
        }
        case TOGGLE_CANCEL_MODAL: {
            return { ...state, cancel_modal: !state.cancel_modal };
        }
        case TOGGLE_CREATE: {
            return { ...state, create: !state.create };
        }
        case SET_VISUAL_EDITOR_DRAGGED_BLOCK_ID: {
            return { ...state, visualEditorDraggedBlockId: action.payload };
        }
        case UNSELECT_VISUAL_EDITOR_BLOCK: {
            return { ...state, visualEditorSelectedBlockId: null };
        }
        case SELECT_VISUAL_EDITOR_BLOCK: {
            return { ...state, visualEditorSelectedBlockId: action.payload };
        }
        case DELETE_VISUAL_EDITOR_BLOCK: {
            return {
                ...state,
                visualEditorSelectedBlockId: null,
                visualEditorDraggedBlockId: null
            };
        }
        case DELETE_VISUAL_EDITOR_BLOCK_FROM_COLUMNS_BLOCK: {
            return {
                ...state,
                visualEditorSelectedBlockId: null,
                visualEditorDraggedBlockId: null
            };
        }
        case SET_CREATE_CLIENT_BRANCH:
            return {
                ...state,
                create_client_branches: action.payload
            };
        case SET_COMPANY: {
            return { ...state, company: action.payload };
        }
        default: return state;
    }
};

const MailTemplateSlice = createSlice({
    name: 'slice',
    initialState: initialMailTemplateSliceState,
    reducers: {
        resetListFilters: (state): void => {
            state.listFilters = initialMailTemplateSliceState.listFilters;
        },
        setAutocomplete: (state, action: PayloadAction<MailTemplateSliceState['autocomplete']>) => {
            state.autocomplete = action.payload;
        },
        setCreateForm: (
            state,
            action: PayloadAction<MailTemplateSliceState['createForm']>
        ): void => {
            state.createForm = action.payload;
        },
        setCreateFormValue<K extends keyof MailTemplateSliceState['createForm']>(
            state: MailTemplateSliceState,
            action: PayloadAction<{
                key: K,
                value: MailTemplateSliceState['createForm'][K]
            }>
        ): void {
            if(action.payload.key === 'mainRecipient') {
                state.createForm.mainRecipientError = '';
            }
            state.createForm[action.payload.key] = action.payload.value;
            state.createForm.mainRecipientError = action.payload.key === 'mainRecipient' ? undefined : state.createForm.mainRecipientError;
            // Server errors are keyed by position: any change to the list makes them stale.
            state.createForm.bccRecipientsErrors = action.payload.key === 'bccRecipients' ? undefined : state.createForm.bccRecipientsErrors;
        },
        setListData: (
            state,
            action: PayloadAction<ListNetworkData>
        ): void => {
            state.listData = action.payload;
        },
        setListFiltersValue<K extends keyof MailTemplateSliceState['listFilters']>(
            state: MailTemplateSliceState,
            action: PayloadAction<{
                key: K,
                value: MailTemplateSliceState['listFilters'][K]
            }>
        ): void {
            set(state.listFilters, action.payload.key, action.payload.value);
        },
        updateMailTemplate: (
            state,
            action: PayloadAction<MailTemplate>
        ): void => {
            if (state.listData.state === 'success') {
                state.listData.data.results = state.listData.data.results.map((item: MailTemplate) => {
                    if (item.id === action.payload.id) {
                        return action.payload;
                    }
                    return item;
                });
            }
        }
    },
});

const VisualEditorReducer = (
    state: MailTemplateVisualEditorState = initialVisualEditorState,
    action: MailTemplateActionTypes
): MailTemplateVisualEditorState => {
    switch (action.type) {
        case ADD_VISUAL_EDITOR_BLOCK: {
            const blockOptions = RegisteredBlocks.find((item) => item.type === action.payload.type);
            const content = state.visualEditorBlocks[action.payload.locale];
            const order = content?.order ?? [];
            const blocks = content?.blocks ?? {};
            const prevBlockIndex = order.findIndex((blockId) => blockId === action.payload.prevBlockId);
            if (blockOptions) {
                const block = blockOptions.factory();
                return {
                    ...state,
                    visualEditorBlocks: {
                        ...state.visualEditorBlocks,
                        [action.payload.locale]: {
                            ...content,
                            order: prevBlockIndex >= 0 ?
                                [...order.slice(0, prevBlockIndex + 1), block.getId(), ...order.slice(prevBlockIndex + 1)] :
                                order.concat([block.getId()]),
                            blocks: {
                                ...blocks,
                                [block.getId()]: block
                            },
                            defaultHtml: state.visualEditorBlocks[action.payload.locale]?.defaultHtml ?? ''
                        }
                    }
                };
            }
            return state;
        }
        case ADD_VISUAL_EDITOR_BLOCKS_WITHOUT_ORDER: {
            const content = state.visualEditorBlocks[action.payload.locale];

            if (content) {
                return {
                    ...state,
                    visualEditorBlocks: {
                        ...state.visualEditorBlocks,
                        [action.payload.locale]: {
                            ...content,
                            blocks: {
                                ...content.blocks,
                                ...mapKeys(
                                    action.payload.blocks,
                                    (block) => block.getId()
                                )
                            }
                        }
                    }
                };
            }

            return state;
        }
        case CREATE_VISUAL_EDITOR_LOCALE_INITIAL_CONTENT: {
            const blocks = state.visualEditorBlocks[action.payload.locale];

            if (!blocks) {
                return {
                    ...state,
                    visualEditorBlocks: {
                        ...state.visualEditorBlocks,
                        [action.payload.locale]: { order: [], blocks: {}, defaultHtml: '' }
                    }
                };
            }

            return state;
        }
        case DELETE_VISUAL_EDITOR_BLOCK: {
            const content = state.visualEditorBlocks[action.payload.locale];

            if (content) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { [action.payload.id]: block, ...rest } = content.blocks;
                return {
                    ...state,
                    visualEditorBlocks: {
                        ...state.visualEditorBlocks,
                        [action.payload.locale]: {
                            order: content.order.filter((item) => item !== action.payload.id),
                            blocks: action.payload.shallow ? content.blocks : rest,
                            defaultHtml: state.visualEditorBlocks[action.payload.locale]?.defaultHtml ?? ''
                        }
                    }
                };
            }

            return state;
        }
        case DELETE_VISUAL_EDITOR_BLOCK_FROM_COLUMNS_BLOCK: {
            const content = state.visualEditorBlocks[action.payload.locale];
            const columnsBlock = content?.blocks[action.payload.columnsBlockId];

            if (content && columnsBlock) {
                const newBlock = columnsBlock.clone(columnsBlock.getOptions()) as ColumnsBlock;
                newBlock.removeChild(action.payload.id);
                return {
                    ...state,
                    visualEditorBlocks: {
                        ...state.visualEditorBlocks,
                        [action.payload.locale]: {
                            ...content,
                            blocks: {
                                ...content.blocks,
                                [action.payload.columnsBlockId]: newBlock
                            }
                        }
                    }
                };
            }

            return state;
        }
        case DUPLICATE_VISUAL_EDITOR_BLOCK: {
            const content = state.visualEditorBlocks[action.payload.locale];
            const selectedBlock = content?.blocks[action.payload.id];
            const selectedBlockIndex = content?.order.findIndex((item) => item === action.payload.id);

            if (content && selectedBlock && isNumber(selectedBlockIndex)) {
                const block = selectedBlock.clone();
                return {
                    ...state,
                    visualEditorBlocks: {
                        ...state.visualEditorBlocks,
                        [action.payload.locale]: {
                            order: [
                                ...content.order.slice(0, selectedBlockIndex + 1),
                                block.getId(),
                                ...content.order.slice(selectedBlockIndex + 1)
                            ],
                            blocks: {
                                ...content.blocks,
                                [block.getId()]: block
                            },
                            defaultHtml: state.visualEditorBlocks[action.payload.locale]?.defaultHtml ?? ''
                        }
                    }
                };
            }

            return state;
        }
        case REORDER_VISUAL_EDITOR_BLOCKS: {
            const content = state.visualEditorBlocks[action.payload.locale];

            if (content) {
                return {
                    ...state,
                    visualEditorBlocks: {
                        ...state.visualEditorBlocks,
                        [action.payload.locale]: {
                            ...content,
                            order: [
                                ...content.order.slice(0, action.payload.index),
                                action.payload.id,
                                ...content.order.slice(action.payload.index)
                            ].filter((item, index) => {
                                return item !== action.payload.id || index === action.payload.index;
                            })
                        }
                    }
                };
            }

            return state;
        }
        case RESET_EDITORS: {
            return {
                ...state,
                visualEditorMode: Mode.DESKTOP,
                visualEditorBlocks: [],
                visualEditorBodyStyles: {
                    fontFamily: defaultFont,
                    backgroundColor: "#fff",
                    width: 600,
                    padding: {
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0
                    },
                    innerBorder: {
                        color: '#000',
                        width: 0
                    },
                    innerBackgroundColor: '#fff',
                    innerPadding: {
                        top: 0,
                        bottom: 0,
                        left: 0,
                        right: 0
                    }
                },
            };
        }
        case RESTORE_VISUAL_EDITOR_BLOCKS_FROM_HTML: {
            if (
                state.visualEditorBlocks[action.payload.locale]?.defaultHtml !== action.payload.html &&
                (
                    !state.visualEditorBlocks[action.payload.locale] ||
                    state.visualEditorBlocks[action.payload.locale]?.order.length === 0
                )
            ) {
                let children: Block<any>[] = [];
                const content = {
                    locale: action.payload.locale,
                    blocks: htmlWalker(
                        action.payload.html,
                        (node) => {
                            const element = node as HTMLElement;

                            if (element.dataset.blockRoot) {
                                return restorer.restoreFromVersion(
                                    extractVersionFromHtml(action.payload.html),
                                    {
                                        type: element.dataset.blockRoot,
                                        html: element.outerHTML,
                                        quotationCode: action.payload.quotationCode,
                                        registerChildren: (blocks) => children = children.concat(blocks)
                                    }
                                );
                            }

                            return null;
                        },
                        new MailTemplateBodyHtmlWalkingStrategy()
                    ).filter((item) => item) as Block<any>[]
                };

                const { document } = createDomObjectsFromHtml(action.payload.html);
                let fontFamily = defaultFont;
                document.querySelectorAll('link').forEach((node) => {
                    const url = new URL(node.href);
                    if (url.searchParams.get('global') === 'true') {
                        fontFamily = {
                            name: url.searchParams.get('family') ?? 'Unknown font',
                            url: node.href
                        };
                    }
                });
                const bodyOptionsElement = document.body.querySelector('div');
                const bodyWrapper: HTMLDivElement | null | undefined = bodyOptionsElement?.querySelector('.body-wrapper');
                const bodyWrapperTD = bodyOptionsElement?.querySelector('td');

                return {
                    ...state,
                    visualEditorBlocks: {
                        ...state.visualEditorBlocks,
                        [action.payload.locale]: {
                            order: content.blocks.map((item) => item.getId()),
                            blocks: {
                                ...mapKeys(
                                    content.blocks,
                                    (block) => block.getId()
                                ),
                                ...mapKeys(
                                    children,
                                    (block) => block.getId()
                                )
                            },
                            defaultHtml: action.payload.html
                        }
                    },
                    visualEditorBodyStyles: {
                        fontFamily,
                        backgroundColor: rgbToHex(
                            bodyOptionsElement?.style.backgroundColor,
                            'rgb(255, 255, 255)'
                        ),
                        width: parseInt(bodyOptionsElement?.dataset.width ?? '200'),
                        padding: {
                            top: parseInt(bodyOptionsElement?.style.paddingTop ?? '0'),
                            bottom: parseInt(bodyOptionsElement?.style.paddingBottom ?? '0'),
                            left: parseInt(bodyOptionsElement?.style.paddingLeft ?? '0'),
                            right: parseInt(bodyOptionsElement?.style.paddingRight ?? '0')
                        },
                        innerBorder: {
                            color: rgbToHex(
                                bodyWrapperTD?.style.borderColor,
                                '#000'
                            ),
                            width: parseInt(bodyWrapperTD?.style.borderWidth ?? '0')
                        },
                        innerBackgroundColor: rgbToHex(
                            bodyWrapper?.style.backgroundColor,
                            '#fff'
                        ),
                        innerPadding: {
                            top: parseInt(bodyWrapper?.style.paddingTop ?? '0'),
                            bottom: parseInt(bodyWrapper?.style.paddingBottom ?? '0'),
                            left: parseInt(bodyWrapper?.style.paddingLeft ?? '0'),
                            right: parseInt(bodyWrapper?.style.paddingRight ?? '0')
                        }
                    }
                };
            }
            return state;
        }
        case SET_VISUAL_EDITOR_BLOCK_OPTIONS: {
            const content = state.visualEditorBlocks[action.payload.locale];
            const block = content?.blocks[action.payload.id];

            if (content && block) {
                const newBlock = block.clone(action.payload.options);
                return {
                    ...state,
                    visualEditorBlocks: {
                        ...state.visualEditorBlocks,
                        [action.payload.locale]: {
                            ...content,
                            blocks: {
                                ...content.blocks,
                                [action.payload.id]: newBlock
                            }
                        }
                    }
                };
            }

            return state;
        }
        case SET_VISUAL_EDITOR_BODY_STYLES: {
            return { ...state, visualEditorBodyStyles: action.payload };
        }
        case SET_VISUAL_EDITOR_MODE: {
            return { ...state, visualEditorMode: action.payload };
        }
        default: return state;
    }
};

export default combineReducers({
    others: MailTemplateReducer,
    [MailTemplateSlice.name]: MailTemplateSlice.reducer,
    visualEditor: undoable(
        VisualEditorReducer,
        {
            undoType: UNDO_VISUAL_EDITOR,
            redoType: REDO_VISUAL_EDITOR,
            filter: includeAction([
                ADD_VISUAL_EDITOR_BLOCK,
                DELETE_VISUAL_EDITOR_BLOCK,
                DUPLICATE_VISUAL_EDITOR_BLOCK,
                REORDER_VISUAL_EDITOR_BLOCKS,
                SET_VISUAL_EDITOR_BLOCK_OPTIONS
            ])
        }
    )
});

export const {
    resetListFilters,
    setAutocomplete,
    setCreateForm,
    setCreateFormValue,
    setListData,
    setListFiltersValue,
    updateMailTemplate
} = MailTemplateSlice.actions;